import React, { useEffect, useRef, useState } from "react";
import "./subjectBox.css";
import SubjectBox from "./SubjectBox";
import { useSelector } from "react-redux";
import axios from "../../../axios";
import { SubjectIcon } from "../../../SubjectIcon";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Subject styling: card gradient/bg, circle bg, circle text color
const subjectStyles = {
  English: {
    cardBg: "linear-gradient(135deg, #FFF0F0 0%, #FFFFFF 100%)",
    circleBg: "#FFE4E6",
    circleColor: "#E11D48",
    initial: "A",
  },
  Hindi: {
    cardBg: "linear-gradient(135deg, #F5F0FF 0%, #FFFFFF 100%)",
    circleBg: "#F3E8FF",
    circleColor: "#9333EA",
    initial: "अ",
  },
  Mathematics: {
    cardBg: "linear-gradient(135deg, #F0FFF4 0%, #FFFFFF 100%)",
    circleBg: "#DCFCE7",
    circleColor: "#16A34A",
    initial: "√x",
  },
  Maths: {
    cardBg: "linear-gradient(135deg, #F0FFF4 0%, #FFFFFF 100%)",
    circleBg: "#DCFCE7",
    circleColor: "#16A34A",
    initial: "√x",
  },
  Science: {
    cardBg: "linear-gradient(135deg, #EFF6FF 0%, #FFFFFF 100%)",
    circleBg: "#DBEAFE",
    circleColor: "#2563EB",
    initial: "⚛",
  },
  Physics: {
    cardBg: "linear-gradient(135deg, #EFF6FF 0%, #FFFFFF 100%)",
    circleBg: "#DBEAFE",
    circleColor: "#2563EB",
    initial: "⚛",
  },
  Chemistry: {
    cardBg: "linear-gradient(135deg, #EFF6FF 0%, #FFFFFF 100%)",
    circleBg: "#DBEAFE",
    circleColor: "#2563EB",
    initial: "⚛",
  },
  Biology: {
    cardBg: "linear-gradient(135deg, #EFF6FF 0%, #FFFFFF 100%)",
    circleBg: "#DBEAFE",
    circleColor: "#2563EB",
    initial: "⚛",
  },
  History: {
    cardBg: "linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)",
    circleBg: "#FEF3C7",
    circleColor: "#D97706",
    initial: "📜",
  },
  "Environmental Studies": {
    cardBg: "linear-gradient(135deg, #F0FDFA 0%, #FFFFFF 100%)",
    circleBg: "#CCFBF1",
    circleColor: "#0D9488",
    initial: "🌐",
  },
  "E.V.S": {
    cardBg: "linear-gradient(135deg, #F0FDFA 0%, #FFFFFF 100%)",
    circleBg: "#CCFBF1",
    circleColor: "#0D9488",
    initial: "🌐",
  },
  EVS: {
    cardBg: "linear-gradient(135deg, #F0FDFA 0%, #FFFFFF 100%)",
    circleBg: "#CCFBF1",
    circleColor: "#0D9488",
    initial: "🌐",
  },
  General: {
    cardBg: "linear-gradient(135deg, #EFF6FF 0%, #FFFFFF 100%)",
    circleBg: "#DBEAFE",
    circleColor: "#2563EB",
    initial: "⚛",
  },
  Social: {
    cardBg: "linear-gradient(135deg, #FFF7ED 0%, #FFFFFF 100%)",
    circleBg: "#FFEDD5",
    circleColor: "#EA580C",
    initial: "🌎",
  },
  Geography: {
    cardBg: "linear-gradient(135deg, #FFF7ED 0%, #FFFFFF 100%)",
    circleBg: "#FFEDD5",
    circleColor: "#EA580C",
    initial: "🗺",
  },
  default: {
    cardBg: "#FFFFFF",
    circleBg: "#F3F4F6",
    circleColor: "#6B7280",
    initial: "?",
  },
};

const getSubjectStyle = (name) => {
  if (!name) return subjectStyles.default;
  const found = Object.keys(subjectStyles).find(
    (key) => key.toLowerCase() === name.toLowerCase(),
  );
  return found ? subjectStyles[found] : subjectStyles.default;
};

const SubjBoxProps = () => {
  const [variable, setVariable] = useState([]);
  const [isGettingData, setIsGettingData] = useState(false);
  const userId = useSelector((state) => state.user?.currentUser?.id);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const helper = async () => {
      try {
        setIsGettingData(true);
        let response = await axios.get(
          `/homework/studentActivity/studentSummary/get/studentId/${userId}`,
        );
        response && setVariable(response.data.data || []);
      } catch (error) {
        console.log(error);
      } finally {
        setIsGettingData(false);
      }
    };
    helper();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => checkOverflow();
    const timer = setTimeout(() => checkOverflow(), 200);
    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", handleResize);
    };
  }, [variable]);

  const checkOverflow = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setIsOverflowing(container.scrollWidth > container.clientWidth);
    }
  };

  const scrollRight = () => {
    scrollContainerRef.current?.scrollBy({ left: 280, behavior: "smooth" });
  };

  const scrollLeft = () => {
    scrollContainerRef.current?.scrollBy({ left: -280, behavior: "smooth" });
  };

  return (
    <div
      className="publish_summery_container gap-3 mb-2 mt-2"
      style={{
        width: "100%",
        backgroundColor: "#f8f9fa",
        overflowX: "auto",
        overflowY: "hidden",
        position: "relative",
      }}
    >
      {/* Loading */}
      {isGettingData ? (
        <div className="text-center w-100 py-3">
          <img
            style={{ width: "80px", height: "70px" }}
            className="ongoing_loader"
            src="/gif/LoaderDot.gif"
            alt=""
          />
        </div>
      ) : (
        <div
          ref={scrollContainerRef}
          className="publish_summery_container mb-4 gap-3 d-flex position-relative"
          style={{
            overflowX: "auto",
            scrollBehavior: "smooth",
            alignItems: "center",
            position: "relative",
            minHeight: "140px",
            padding: "8px 4px",
          }}
        >
          {/* Left Sticky Button */}
          {isOverflowing && (
            <button
              type="button"
              className="btn btn-light d-flex align-items-center justify-content-center shadow-sm"
              onClick={scrollLeft}
              style={{
                position: "sticky",
                left: 4,
                top: "50%",
                transform: "translateY(-50%)",
                borderRadius: "50%",
                backgroundColor: "#fff",
                width: "36px",
                height: "36px",
                border: "1px solid #e5e7eb",
                zIndex: 10,
                flexShrink: 0,
              }}
            >
              <ChevronLeft color="#6B7280" size={20} />
            </button>
          )}

          {/* Cards */}
          {variable.map((obj, index) => {
            const style = getSubjectStyle(obj?.subject?.name);
            const iconSrc =
              SubjectIcon?.find((i) => i.subjectName === obj?.subject?.name)
                ?.icon || null;

            return (
              <div
                key={index}
                className="shadow-sm"
                style={{
                  minWidth: "210px",
                  maxWidth: "250px",
                  background: style.cardBg,
                  borderRadius: "16px",
                  border: "1px solid rgba(0,0,0,0.04)",
                  padding: "16px",
                  flexShrink: 0,
                }}
              >
                {/* Top row: Icon Circle + Subject Name */}
                <div
                  className="d-flex align-items-center gap-3"
                  style={{ marginBottom: "14px" }}
                >
                  {/* Icon Circle */}
                  <div
                    style={{
                      width: "44px",
                      height: "44px",
                      minWidth: "44px",
                      minHeight: "44px",
                      borderRadius: "50%",
                      backgroundColor: style.circleBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      flexShrink: 0,
                    }}
                  >
                    {iconSrc ? (
                      <img
                        src={iconSrc}
                        alt=""
                        style={{
                          width: "22px",
                          height: "22px",
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    ) : (
                      <span
                        style={{
                          color: style.circleColor,
                          fontSize: "15px",
                          fontWeight: 700,
                          lineHeight: 1,
                          display: "block",
                        }}
                      >
                        {style.initial}
                      </span>
                    )}
                  </div>

                  {/* Subject Name */}
                  <h6
                    className="m-0 fw-bold text-truncate"
                    title={obj?.subject?.name}
                    style={{
                      fontSize: "15px",
                      color: "#1f2937",
                      letterSpacing: "-0.01em",
                      lineHeight: 1.3,
                    }}
                  >
                    {obj?.subject?.name}
                  </h6>
                </div>

                {/* Divider */}
                <div
                  style={{
                    height: "1px",
                    backgroundColor: "rgba(0,0,0,0.06)",
                    marginBottom: "12px",
                  }}
                />

                {/* Stats Row */}
                <div className="d-flex justify-content-between text-center">
                  <div
                    className="d-flex flex-column align-items-center"
                    style={{ flex: 1 }}
                  >
                    <small
                      style={{
                        fontSize: "11px",
                        color: "#9CA3AF",
                        fontWeight: 500,
                        marginBottom: "4px",
                        display: "block",
                      }}
                    >
                      Published
                    </small>
                    <span
                      style={{
                        fontSize: "18px",
                        fontWeight: 700,
                        color: "#111827",
                        lineHeight: 1,
                        display: "block",
                      }}
                    >
                      {obj?.not_started === 0
                        ? obj?.not_started
                        : String(obj?.not_started).padStart(2, "0")}
                    </span>
                  </div>

                  <div
                    className="d-flex flex-column align-items-center"
                    style={{
                      flex: 1,
                      borderLeft: "1px solid rgba(0,0,0,0.06)",
                      borderRight: "1px solid rgba(0,0,0,0.06)",
                      padding: "0 4px",
                    }}
                  >
                    <small
                      style={{
                        fontSize: "11px",
                        color: "#9CA3AF",
                        fontWeight: 500,
                        marginBottom: "4px",
                        display: "block",
                      }}
                    >
                      On Review
                    </small>
                    <span
                      style={{
                        fontSize: "18px",
                        fontWeight: 700,
                        color: "#111827",
                        lineHeight: 1,
                        display: "block",
                      }}
                    >
                      {obj?.on_review === 0
                        ? obj?.on_review
                        : String(obj?.on_review).padStart(2, "0")}
                    </span>
                  </div>

                  <div
                    className="d-flex flex-column align-items-center"
                    style={{ flex: 1 }}
                  >
                    <small
                      style={{
                        fontSize: "11px",
                        color: "#9CA3AF",
                        fontWeight: 500,
                        marginBottom: "4px",
                        display: "block",
                      }}
                    >
                      Completed
                    </small>
                    <span
                      style={{
                        fontSize: "18px",
                        fontWeight: 700,
                        color: "#111827",
                        lineHeight: 1,
                        display: "block",
                      }}
                    >
                      {obj?.completed === 0
                        ? obj?.completed
                        : String(obj?.completed).padStart(2, "0")}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Right Sticky Button */}
          {isOverflowing && (
            <button
              type="button"
              className="btn btn-light d-flex align-items-center justify-content-center shadow-sm"
              onClick={scrollRight}
              style={{
                position: "sticky",
                right: 4,
                top: "50%",
                transform: "translateY(-50%)",
                borderRadius: "50%",
                backgroundColor: "#fff",
                width: "36px",
                height: "36px",
                border: "1px solid #e5e7eb",
                zIndex: 10,
                flexShrink: 0,
              }}
            >
              <ChevronRight color="#6B7280" size={20} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SubjBoxProps;
